/***
 *
 *   CARD
 *   Displays a card with header, content, and footer.
 *
 *   DOCS
 *   https://docs.useMapMetrics Atlas.app/MapMetrics Atlas-web/components/card
 *   https://ui.shadcn.com/docs/components/card
 *
 *   PROPS
 *   center: align the card in the center of its parent (boolean, optional)
 *   children: children to render (component(s), required)
 *   className: custom class (SCSS or tailwind style, optional)
 *   description: header description (string, optional)
 *   loading: toggle the loading animation (boolean, optional)
 *   title: header title (string, optional)
 *
 **********/

import { Fragment, forwardRef, Children, isValidElement } from "react";
import { cn, Loader } from "components/lib";

const Card = forwardRef(
  (
    { className, title, loading, description, center, children, ...props },
    ref
  ) => {
    let footer = null;

    const childrenToRender = Children.map(children, (child) => {
      if (isValidElement(child) && child.type.name === "CardFooter") {
        // remove footer from the normal children
        footer = child;
        return null;
      }

      return child;
    });

    const style = cn(
      {
        "relative p-6 sm:p-8 lg:p-10 rounded-2xl text-white shadow-lg glass-card dark:glass-card-dark glass-hover dark:glass-hover-dark transition-all duration-300 overflow-x-hidden": true,
        "mx-auto": center,
        "h-72 [&>div]:mt-4": loading,
      },
      className
    );
    return (
      <div ref={ref} className={style} {...props}>
        {loading ? (
          <Loader />
        ) : (
          <Fragment>
            {(title || description) && (
              <CardHeader>
                {title && <CardTitle>{title}</CardTitle>}

                {description && (
                  <CardDescription>{description}</CardDescription>
                )}
              </CardHeader>
            )}

            {childrenToRender && <CardContent>{children}</CardContent>}

            {footer}
          </Fragment>
        )}
      </div>
    );
  }
);

Card.displayName = "Card";

const CardHeader = forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-between mb-6 pb-4 border-solid border-white/20 border-b fade-in",
      className
    )}
    {...props}
  />
));

CardHeader.displayName = "CardHeader";

const CardTitle = forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "block text-xl sm:text-2xl font-light leading-tight tracking-wide text-white",
      className
    )}
    {...props}
  />
));

CardTitle.displayName = "CardTitle";

const CardDescription = forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("block text-white/70 mt-2 font-light tracking-wide", className)}
    {...props}
  />
));

CardDescription.displayName = "CardDescription";

const CardContent = forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "slide-up [&>h1]:mt-4 [&>h1]:font-light [&>h1]:text-lg [&>h1]:text-white [&>span]:block [&>span]:mb-2 [&>span]:text-white/80",
      className
    )}
    style={{animationDelay: '0.1s'}}
    {...props}
  />
));

CardContent.displayName = "CardContent";

const CardFooter = forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
));

CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
